/**
 * Note: If the role is a pupil, only single selects can be made.
 * 'selectedBaseLevelIds' and 'selectedSubLevelIds' will be set with
 * a single entry.
 *
 * If the role is set as teacher. The user will be able to multiselect
 * levels and sublevels hence the array.
 *
 */

import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { RootState } from '~/types/store'
import { User, UserLevel } from '~/types/user'
import { SubjectsState } from '~/store/subjects'

type OnboardingArrayField = 'selectedBaseLevelIds' | 'selectedSubLevelIds' | 'selectedSubjectIds' | 'selectedHasAccessIds'
type OnboardingSingularField = 'educationRole' | 'profile' | 'examYear'

export interface OnboardingState {
  educationRole: string
  selectedBaseLevelIds: string[]
  selectedSubLevelIds: string[]
  profile: string
  selectedSubjectIds: Array<number | string>
  selectedHasAccessIds: Array<number | string>
  examYear: string
}

export function state (): OnboardingState {
  return ({
  educationRole: 'pupil',
  selectedBaseLevelIds: [], // 'e.g vmbo, havo, vwo'
  selectedSubLevelIds: [], // 'e.g vmbo-gl-tl, vmbo-k, vmbo-b'
  profile: '',
  selectedSubjectIds: [],
  selectedHasAccessIds: [],
  examYear: ''
})
}

export const mutations: MutationTree<OnboardingState> = {
  SET_SINGULAR (state, data: { field: OnboardingSingularField, value: string }) {
    state[data.field] = data.value !== state[data.field] ? data.value : ''
  },
  SET_ARRAY (state, data: { field: OnboardingArrayField, value: number | string }) {
    const target = state[data.field] as Array<number | string>
    if (target.includes(data.value)) {
      target.splice(target.indexOf(data.value), 1)
    } else {
      target.push(data.value)
    }
  },
  SET_SELECTED_BASE_LEVEL (state, data: string[]) {
    if (state.selectedBaseLevelIds.includes(data[0])) {
      state.selectedBaseLevelIds.splice(state.selectedBaseLevelIds.indexOf(data[0]), 1)
    } else {
      state.selectedBaseLevelIds = data
    }
  },
  SET_SELECTED_SUB_LEVEL (state, data: string[]) {
    if (state.selectedSubLevelIds.includes(data[0])) {
      state.selectedSubLevelIds.splice(state.selectedSubLevelIds.indexOf(data[0]), 1)
    } else {
      state.selectedSubLevelIds = data
    }
  },
  SET_SELECTED_SUBJECTS (state, data: Array<number | string>) {
    state.selectedSubjectIds = data
  },
  SET_SELECTED_HAS_ACCESS_IDS (state, data: Array<number | string>) {
    state.selectedHasAccessIds = data
  },
  SET_SELECTED_EXAM_YEAR (state, data: string) {
    state.examYear = data
  }
}

export const getters: GetterTree<OnboardingState, RootState> = {
  requiredSubjectsIds (state, getters, rootState) {
    const subjects = (rootState as RootState & { subjects: SubjectsState }).subjects
    if (!subjects.primarySubjects.length) return []
    return subjects.primarySubjects.filter(subject => subject.required).map(item => item.id)
  },
  allSelectedLevels (state) {
    // Filter out 'vmbo' because we use the sublevels of vmbo to make the calls but
    // need it  in the base level array for the selection
    const baseLevels = state.selectedBaseLevelIds.filter(level => level !== 'vmbo')
    const subLevels = state.selectedSubLevelIds
    return [...baseLevels, ...subLevels]
  }
}

export const actions: ActionTree<OnboardingState, RootState> = {
  async submit ({ state, dispatch, getters }) {
    try {
      // response is the user object
      await dispatch('auth/updateUserLevels', { levels: getters.allSelectedLevels }, { root: true })
      const user: User = await dispatch('auth/updateUser', {
        educationRole: state.educationRole,
        onboardingCompleted: true,
        profile: state.profile,
        examYear: state.examYear
      }, { root: true })

      if (user) {
        // Remove existing user-level class
        document.documentElement.classList.forEach((className) => {
          if (className.startsWith('user-level')) {
            document.documentElement.classList.remove(className)
          }
        })
        // Levels key has changed to an array with levels because a teacher can choose multiple levels
        // In this case, we selected the first level from the array and return that classname.
        const firstLevelNameAsClass = (levelArray: UserLevel[] = []) => {
          return !levelArray.length ? 'user-level-havo' : `user-level-${levelArray[0].name.split('-')[0]}`
        }
        document.documentElement.classList.add(firstLevelNameAsClass(user.levels))
      }

      await dispatch('auth/updateUserSubjects', { ids: state.selectedSubjectIds }, { root: true })
      await dispatch('auth/getUser', null, { root: true })
    } catch (err) {
      console.log(err)
    }
  }
}
